/** MCP connection (B07-001 scoped keys shown once, B07-002 tool calls obey scope + approvals, B07-003 BYO LLM key). */
import { useState, type FormEvent } from 'react';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { MCP_SCOPES, MCP_TOOLS, type McpScope } from '@qm/contracts/mcp';
import { AGENCY_LLM_ALLOWLIST } from '@qm/contracts/workspace';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, ErrorNote, Field, Loadable, Notice, Screen, Tabs, When } from '../kit/ui';
import type { McpKeyCreated } from '../api';
import { LLM_PROVIDERS, isReadOnly, keyStatus, llmAllowed, toggleScope } from '../lib/logic';
import { usePartnerApi } from './layout';
import { useSearchParams } from 'react-router';

export function McpPage() {
  const { t } = useSurface();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') ?? 'keys';
  return (
    <Screen title={t('portal.partners.mcp.title')} subtitle={t('portal.partners.mcp.subtitle')} testId="mcp">
      <Tabs label={t('portal.partners.mcp.title')} active={tab} onSelect={(v) => setSp({ tab: v })}
        tabs={[{ id: 'keys', label: t('portal.partners.mcp.tab.keys') }, { id: 'tools', label: t('portal.partners.mcp.tab.tools') }, { id: 'llm', label: t('portal.partners.mcp.tab.llm') }]} />
      {tab === 'tools' ? <ToolCatalogue /> : tab === 'llm' ? <LlmKeys /> : <McpKeys />}
    </Screen>
  );
}

export function McpKeys() {
  const { t, now } = useSurface();
  const api = usePartnerApi();
  const keys = useResource('mcp:keys', api.mcpKeys);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<McpScope[]>(['read:clients']);
  const [days, setDays] = useState(90);
  const [shown, setShown] = useState<McpKeyCreated | null>(null);
  const create = useAction(api.createMcpKey);
  const revoke = useAction(api.revokeMcpKey);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await create.run({ name: name.trim(), scopes, expiresInDays: days });
    if (r) { setShown(r); setName(''); keys.reload(); }
  };
  return (
    <>
      {shown ? (
        <Panel data-testid="key-once">
          <Heading level={2}>{t('portal.partners.mcp.copyNow')}</Heading>
          <Text tone="muted">{t('portal.partners.mcp.shownOnce')}</Text>
          <code className="qx-secret" data-testid="key-secret">{shown.secret}</code>
          <Button onClick={() => setShown(null)}>{t('portal.partners.mcp.done')}</Button>
        </Panel>
      ) : null}
      <Panel>
        <form onSubmit={submit}>
          <Field label={t('portal.partners.mcp.name')}>{(id) => <input id={id} name="name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <fieldset><legend>{t('portal.partners.mcp.scopes')}</legend>
            {MCP_SCOPES.map((s) => <Checkbox key={s} name={s} label={t(`portal.partners.mcp.scope.${s.replace(':', '_')}`)} checked={scopes.includes(s)} onChange={(v) => setScopes((x) => toggleScope(x, s, v))} />)}
          </fieldset>
          <Field label={t('portal.partners.mcp.expiry')}>{(id) => <select id={id} value={days} onChange={(e) => setDays(Number(e.target.value))}>{[30, 90, 180, 365].map((d) => <option key={d} value={d}>{t('portal.partners.mcp.days', { n: d })}</option>)}</select>}</Field>
          <Button type="submit" variant="primary" disabled={name.trim().length < 2 || scopes.length === 0 || create.pending}>{t('portal.partners.mcp.issue')}</Button>
        </form>
        <ErrorNote error={create.error} />
      </Panel>
      <Loadable resource={keys}>
        {(rows) => <DataTable rows={rows} rowKey={(k) => k.id} empty={t('portal.kit.empty')} columns={[
          { key: 'name', header: t('portal.partners.mcp.name'), render: (k) => <>{k.name} <code>{k.prefix}...</code></> },
          { key: 'scopes', header: t('portal.partners.mcp.scopes'), render: (k) => <>{k.scopes.join(', ')} {isReadOnly(k.scopes) ? <Badge>{t('portal.partners.mcp.readOnly')}</Badge> : null}</> },
          { key: 'status', header: t('portal.partners.clients.col.status'), render: (k) => { const s = keyStatus(k, now()); return <Badge tone={s === 'active' ? 'good' : 'bad'} testId={`key-${s}`}>{t(`portal.partners.mcp.status.${s}`)}</Badge>; } },
          { key: 'exp', header: t('portal.partners.mcp.expires'), narrow: 'hide', render: (k) => <When iso={k.expiresAt} /> },
          { key: 'used', header: t('portal.partners.mcp.lastUsed'), narrow: 'hide', render: (k) => <When iso={k.lastUsedAt} /> },
          { key: 'act', header: '', render: (k) => (keyStatus(k, now()) === 'active' ? <Button onClick={async () => { if (await revoke.run(k.id)) keys.reload(); }}>{t('portal.partners.mcp.revoke')}</Button> : null) },
        ]} />}
      </Loadable>
      <Text tone="muted">{t('portal.partners.mcp.revokeNote')}</Text>
      <ErrorNote error={revoke.error} />
    </>
  );
}

function ToolCatalogue() {
  const { t } = useSurface();
  return (
    <div data-testid="mcp-tools">
      <Notice>{t('portal.partners.mcp.approvalNote')}</Notice>
      <DataTable rows={MCP_TOOLS} rowKey={(x) => x.name} empty="" columns={[
        { key: 'name', header: t('portal.partners.mcp.tool'), render: (x) => <code>{x.name}</code> },
        { key: 'scope', header: t('portal.partners.mcp.requiredScope'), render: (x) => x.scope },
        { key: 'desc', header: t('portal.partners.mcp.description'), render: (x) => t(`portal.partners.mcp.toolDesc.${x.name}`) },
      ]} />
    </div>
  );
}

export function LlmKeys() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const list = useResource('llm:list', api.llmConnections);
  const [provider, setProvider] = useState(LLM_PROVIDERS[0] ?? '');
  const [model, setModel] = useState(AGENCY_LLM_ALLOWLIST[LLM_PROVIDERS[0] ?? '']?.[0] ?? '');
  const [apiKey, setApiKey] = useState('');
  const add = useAction(api.addLlmConnection);
  const remove = useAction(api.removeLlmConnection);
  const allowed = llmAllowed(provider, model);
  return (
    <div data-testid="llm-keys">
      <Text tone="muted">{t('portal.partners.llm.allowlistNote')}</Text>
      <form onSubmit={async (e) => { e.preventDefault(); if (allowed && (await add.run({ provider, model, apiKey }))) { setApiKey(''); list.reload(); } }}>
        <Field label={t('portal.partners.llm.provider')}>{(id) => <select id={id} value={provider} onChange={(e) => { setProvider(e.target.value); setModel(AGENCY_LLM_ALLOWLIST[e.target.value]?.[0] ?? ''); }}>{LLM_PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}</select>}</Field>
        <Field label={t('portal.partners.llm.model')}>{(id) => <select id={id} value={model} onChange={(e) => setModel(e.target.value)}>{(AGENCY_LLM_ALLOWLIST[provider] ?? []).map((m) => <option key={m} value={m}>{m}</option>)}</select>}</Field>
        <Field label={t('portal.partners.llm.key')} hint={t('portal.partners.llm.keyHint')}>{(id) => <input id={id} type="password" autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />}</Field>
        <Button type="submit" variant="primary" disabled={!allowed || apiKey.length < 16 || add.pending}>{t('portal.partners.llm.add')}</Button>
      </form>
      <ErrorNote error={add.error} />
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rows} rowKey={(c) => c.id} empty={t('portal.kit.empty')} columns={[
          { key: 'p', header: t('portal.partners.llm.provider'), render: (c) => c.provider },
          { key: 'm', header: t('portal.partners.llm.model'), render: (c) => c.model },
          { key: 'k', header: t('portal.partners.llm.key'), render: (c) => <code>...{c.keyHint}</code> },
          { key: 'a', header: '', render: (c) => <Button onClick={async () => { if (await remove.run(c.id)) list.reload(); }}>{t('portal.partners.llm.remove')}</Button> },
        ]} />}
      </Loadable>
    </div>
  );
}
